import { DecrementSvg } from '@forwardflow/assets/svg/DecrementSvg'
import { IncrementSvg } from '@forwardflow/assets/svg/IncrementSvg'
import { SortArrowDownSvg } from '@forwardflow/assets/svg/SortArrowDownSvg'
import { SortArrowUpDown } from '@forwardflow/assets/svg/SortArrowUpDown'
import { SortArrowUpSvg } from '@forwardflow/assets/svg/SortArrowUpSvg'
import { useGetDashboardChartsLeadSourceBreakdown } from '@forwardflow/hooks/customHooks'
import {
   DashboardChartLeadSourceBreakdownStats,
   TimePeriod,
} from '@forwardflow/types'
import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { CoveragePieSVG } from './CoveragePieSVG'

export function LeadSourcesBreakdown() {
   const [searchParams, setSearchParams] = useSearchParams()
   const timePeriod = searchParams.get('timePeriod') || 'allTime'
   const [page, setPage] = useState(1)
   const perPage = 10

   const initialSortBy:
      | 'source'
      | 'totalLeads'
      | 'newLeads'
      | 'appointments'
      | 'closed'
      | 'forwardFlowCoverage' =
      (searchParams.get('sortBy') as
         | 'source'
         | 'totalLeads'
         | 'newLeads'
         | 'appointments'
         | 'closed'
         | 'forwardFlowCoverage') || 'totalLeads'
   const initialSortOrder =
      (searchParams.get('sortOrder') as 'asc' | 'desc') || 'desc'
   const [sortBy, setSortBy] = useState<typeof initialSortBy>(initialSortBy)
   const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(initialSortOrder)
   const [rows, setRows] = useState<DashboardChartLeadSourceBreakdownStats[]>(
      []
   )
   const [hasMore, setHasMore] = useState(true)
   const tableRef = useRef<HTMLDivElement>(null)

   const {
      data: DashboardChartsLeadSourceBreakdown,
      isLoading: DashboardChartsLeadSourceBreakdownLoading,
   } = useGetDashboardChartsLeadSourceBreakdown({
      page,
      sortBy,
      sortOrder,
      timePeriod: timePeriod as TimePeriod,
      perPage,
   })

   useEffect(() => {
      if (!DashboardChartsLeadSourceBreakdown) {
         return
      }
      if (page === 1) {
         setRows(DashboardChartsLeadSourceBreakdown?.data || [])
      } else if (DashboardChartsLeadSourceBreakdown?.data) {
         setRows((prev) => [
            ...prev,
            ...DashboardChartsLeadSourceBreakdown.data,
         ])
      }
      setHasMore(DashboardChartsLeadSourceBreakdown?.meta?.hasNextPage ?? false)
   }, [DashboardChartsLeadSourceBreakdown, page])

   useEffect(() => {
      const handleScroll = () => {
         if (
            !tableRef.current ||
            DashboardChartsLeadSourceBreakdownLoading ||
            !hasMore
         )
            return
         const { scrollTop, scrollHeight, clientHeight } = tableRef.current
         if (scrollHeight - scrollTop - clientHeight < 100) {
            setPage((p) => (hasMore ? p + 1 : p))
         }
      }
      const ref = tableRef.current
      if (ref) ref.addEventListener('scroll', handleScroll)
      return () => {
         if (ref) ref.removeEventListener('scroll', handleScroll)
      }
   }, [DashboardChartsLeadSourceBreakdownLoading, hasMore])

   // Reset pagination on sort/filter change
   useEffect(() => {
      setPage(1)
   }, [sortBy, sortOrder, timePeriod])

   useEffect(() => {
      setSortBy((searchParams.get('sortBy') as typeof sortBy) || 'totalLeads')
      setSortOrder((searchParams.get('sortOrder') as 'asc' | 'desc') || 'desc')
   }, [searchParams])

   const handleSort = (column: typeof sortBy) => {
      let newSortOrder = sortOrder
      let newSortBy = sortBy
      if (sortBy === column) {
         newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc'
      } else {
         newSortBy = column
         newSortOrder = 'desc'
      }
      setSortBy(newSortBy)
      setSortOrder(newSortOrder)
      setSearchParams((prev) => {
         const params = new URLSearchParams(prev)
         params.set('sortBy', newSortBy)
         params.set('sortOrder', newSortOrder)
         return params
      })
      setPage(1)
   }

   return (
      <div className="px-6 py-4 shadow-lg bg-white/5 rounded-xl">
         <div className="px-3 mb-4 text-lg font-semibold text-white">
            Lead Sources Breakdown
         </div>
         <div ref={tableRef} className="overflow-x-auto max-h-[58vh]">
            <table className="min-w-full text-sm text-white">
               <thead>
                  <tr className="h-14 text-white/70 sticky top-0 z-10 bg-[#262626] border-b-2 border-black/20 ">
                     <th
                        className="w-[13%] px-4 py-2 font-normal text-left cursor-pointer"
                        onClick={() => handleSort('source')}
                     >
                        <div className="flex items-center gap-1">
                           <span> Lead Source </span>
                           {sortBy === 'source' ? (
                              sortOrder !== 'asc' ? (
                                 <SortArrowUpSvg />
                              ) : (
                                 <SortArrowDownSvg />
                              )
                           ) : (
                              <SortArrowUpDown />
                           )}
                        </div>
                     </th>
                     <th
                        className="w-1/6 px-4 py-2 font-normal text-left cursor-pointer "
                        onClick={() => handleSort('totalLeads')}
                     >
                        <div className="flex items-center justify-center gap-1">
                           <span> Total Leads</span>
                           {sortBy === 'totalLeads' ? (
                              sortOrder === 'asc' ? (
                                 <SortArrowUpSvg />
                              ) : (
                                 <SortArrowDownSvg />
                              )
                           ) : (
                              <SortArrowUpDown />
                           )}
                        </div>
                     </th>
                     <th
                        className="w-1/6 px-4 py-2 font-normal text-left cursor-pointer "
                        onClick={() => handleSort('newLeads')}
                     >
                        <div className="flex items-center justify-center gap-1">
                           <span> New Leads </span>
                           {sortBy === 'newLeads' ? (
                              sortOrder === 'asc' ? (
                                 <SortArrowUpSvg />
                              ) : (
                                 <SortArrowDownSvg />
                              )
                           ) : (
                              <SortArrowUpDown />
                           )}
                        </div>
                     </th>
                     <th
                        className="w-1/6 px-4 py-2 font-normal text-left cursor-pointer "
                        onClick={() => handleSort('appointments')}
                     >
                        <div className="flex items-center justify-center gap-1">
                           <span> Appointments </span>
                           {sortBy === 'appointments' ? (
                              sortOrder === 'asc' ? (
                                 <SortArrowUpSvg />
                              ) : (
                                 <SortArrowDownSvg />
                              )
                           ) : (
                              <SortArrowUpDown />
                           )}
                        </div>
                     </th>
                     <th
                        className="w-1/6 px-4 py-2 font-normal text-left cursor-pointer "
                        onClick={() => handleSort('closed')}
                     >
                        <div className="flex items-center justify-center gap-1">
                           <span> Closed (Closure Rate) </span>
                           {sortBy === 'closed' ? (
                              sortOrder === 'asc' ? (
                                 <SortArrowUpSvg />
                              ) : (
                                 <SortArrowDownSvg />
                              )
                           ) : (
                              <SortArrowUpDown />
                           )}
                        </div>
                     </th>
                     <th
                        className="px-4 py-2 font-normal text-left cursor-pointer "
                        onClick={() => handleSort('forwardFlowCoverage')}
                     >
                        <div className="flex items-center justify-center gap-1">
                           <span> FF Coverage </span>
                           {sortBy === 'forwardFlowCoverage' ? (
                              sortOrder === 'asc' ? (
                                 <SortArrowUpSvg />
                              ) : (
                                 <SortArrowDownSvg />
                              )
                           ) : (
                              <SortArrowUpDown />
                           )}
                        </div>
                     </th>
                  </tr>
               </thead>
               <tbody>
                  {rows.length === 0 &&
                  !DashboardChartsLeadSourceBreakdownLoading ? (
                     <tr>
                        <td colSpan={6} className="py-6 text-center">
                           No data found.
                        </td>
                     </tr>
                  ) : (
                     rows.map((row: any, idx: number) => (
                        <tr
                           key={row.source + idx}
                           className={`bg-[#D9D9D9]/5 w-full h-[82px] rounded ${
                              idx !== rows.length - 1
                                 ? ' border-b-[10px] border-black/20'
                                 : ''
                           }`}
                        >
                           <td className="px-5 py-3 text-[18px] font-medium">
                              {row.source}
                           </td>
                           <td className="px-4 py-3 font-medium text-center">
                              {row.totalLeads.value?.toLocaleString() ?? '-'}
                           </td>
                           <td className="px-4 py-3">
                              <div className="flex items-center justify-center gap-2">
                                 <span className="text-[#FF914D]">
                                    {row.newLeads.value?.toLocaleString() ??
                                       '-'}
                                 </span>
                                 {row.newLeads.change !== null && (
                                    <span className="flex items-center gap-1 text-[14px]">
                                       {row.newLeads.change.isIncrement ? (
                                          <IncrementSvg />
                                       ) : (
                                          <DecrementSvg />
                                       )}
                                       <span
                                          className={
                                             row.newLeads.change.isIncrement
                                                ? 'text-[#39C95F]'
                                                : 'text-[#FF5185]'
                                          }
                                       >
                                          {row.newLeads.change.count}
                                       </span>
                                    </span>
                                 )}
                              </div>
                           </td>
                           <td className="px-4 py-3">
                              <div className="flex items-center justify-center gap-2">
                                 <span className="text-[#FFD600]">
                                    {row.appointments.value?.toLocaleString() ??
                                       '-'}
                                 </span>
                                 {row.appointments.change !== null && (
                                    <span className="flex items-center gap-1  text-[14px]">
                                       {row.appointments.change.isIncrement ? (
                                          <IncrementSvg />
                                       ) : (
                                          <DecrementSvg />
                                       )}
                                       <span
                                          className={
                                             row.appointments.change.isIncrement
                                                ? 'text-[#39C95F]'
                                                : 'text-[#FF5185]'
                                          }
                                       >
                                          {row.appointments.change.count}
                                       </span>
                                    </span>
                                 )}
                              </div>
                           </td>
                           <td className="gap-2 px-4 py-3 ">
                              <div className="flex items-center justify-center gap-2">
                                 <span className="text-[#00FFD1]">
                                    {row.closed.value?.toLocaleString() ?? '-'}
                                 </span>
                                 <span className="p-1 text-white rounded bg-white/5">
                                    {row.closed.closureRate
                                       ? `${row.closed.closureRate.toFixed(2)}%`
                                       : '-'}
                                 </span>
                                 {row.closed.change !== null && (
                                    <span className="flex items-center gap-1 text-[14px]">
                                       {row.closed.change.isIncrement ? (
                                          <IncrementSvg />
                                       ) : (
                                          <DecrementSvg />
                                       )}
                                       <span
                                          className={
                                             row.closed.change.isIncrement
                                                ? 'text-[#39C95F]'
                                                : 'text-[#FF5185]'
                                          }
                                       >
                                          {Math.abs(row.closed.change.count)}
                                       </span>
                                    </span>
                                 )}
                              </div>
                           </td>
                           <td className="px-4 py-3">
                              <div className="flex items-center justify-center">
                                 {!row.forwardFlowCoverage.value ? (
                                    <span className="w-10 h-[1px] bg-gray-400"></span>
                                 ) : (
                                    <CoveragePieSVG
                                       percent={
                                          row.forwardFlowCoverage.value ?? 0
                                       }
                                    />
                                 )}
                              </div>
                           </td>
                        </tr>
                     ))
                  )}
                  {DashboardChartsLeadSourceBreakdownLoading && (
                     <tr>
                        <td colSpan={6} className="py-6 text-center">
                           Loading...
                        </td>
                     </tr>
                  )}
               </tbody>
            </table>
         </div>
      </div>
   )
}
